import React, { useState } from "react";
import ioniconaaddoutline from "../../assets/dashboard/ioniconaaddoutline.svg";
import closeDowpDowniconimg from "../../assets/task/closedropdown-cicon.svg";
import SearchableInputsDropdown from "./SearchableDropdown";
import InputsDropdownMultipleSearchSelect from "./InputsDropdownMultipleSearchSelect";

type Attendee = {
  name: string;
  email: string;
  uuid?: string;
  client_location_id?: string;
};

type Agenda = string;

export type AgencyOption = {
  id: string;
  name: string;
};

export type EmployeeOption = {
  id: string;
  /** Formatted as "last_name, middle_name first_name" */
  displayName: string;
  email: string;
  accessType: string;
  uuid?: string;
};

type Props = {
  value: any[];
  onChange: (items: any[]) => void;
  isSingle: boolean;
  isClientMeeting?: boolean;
  agencies?: AgencyOption[];
  agenciesLoading?: boolean;
  onAgencySelect?: (agencyId: string) => void;
  employees?: EmployeeOption[];
  employeesLoading?: boolean;
  onPendingCountChange?: (count: number) => void;
  isDemoMeeting?:any
};

const ExternalAttendees: React.FC<Props> = ({
  value,
  onChange,
  isSingle,
  isClientMeeting = false,
  agencies = [],
  agenciesLoading = false,
  onAgencySelect,
  employees = [],
  employeesLoading = false,
  onPendingCountChange,
  isDemoMeeting
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [agenda, setAgenda] = useState("");

  const [selectedAgencyId, setSelectedAgencyId] = useState("");
  const [selectedAgencyName, setSelectedAgencyName] = useState("");
  const [checkedEmployees, setCheckedEmployees] = useState<{ key: string; name: string }[]>([]);

  // Notify parent whenever pending count changes
  React.useEffect(() => {
    onPendingCountChange?.(checkedEmployees.length);
  }, [checkedEmployees.length]);

  // name includes email so the built-in search filters on both
  const employeeOptions = employees.map((emp) => ({
    key: emp.id,
    name: `${emp.displayName} (${emp.email})`,
    type: emp.accessType || undefined,
  }));

  const handleAdd = () => {
    if (isSingle) {
      if (!agenda.trim()) return;
      onChange([...(value as Agenda[]), agenda]);
      setAgenda("");
    } else {
      if (!name.trim() || !email.trim()) return;
      const alreadyAdded = (value as Attendee[]).some(
        (a) => a.email.toLowerCase() === email.toLowerCase()
      );
      if (alreadyAdded) return;
      onChange([...(value as Attendee[]), { name, email }]);
      setName("");
      setEmail("");
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleAgencySelect = (key: string, agName: string) => {
    setSelectedAgencyId(key);
    setSelectedAgencyName(agName);
    setCheckedEmployees([]);
    onAgencySelect?.(key);
  };

  const handleEmployeeSelect = (key: string, nameOrBool: any) => {
    if (key === "all") {
      const allSelected = nameOrBool as boolean;
      setCheckedEmployees(allSelected ? [] : employeeOptions.map((o) => ({ key: o.key, name: o.name })));
    } else {
      setCheckedEmployees((prev) => {
        const exists = prev.some((it) => it.key === key);
        return exists ? prev.filter((it) => it.key !== key) : [...prev, { key, name: nameOrBool }];
      });
    }
  };

  const handleAddCheckedEmployees = () => {
    const existing = value as Attendee[];
    const existingEmails = new Set(existing.map((a) => a.email.toLowerCase()));
    const toAdd: Attendee[] = checkedEmployees
      .map((checked) => {
        const emp = employees.find((e) => e.id === checked.key);
        if (!emp) return null;
        return {
          name: emp.displayName,
          email: emp.email,
          uuid: emp.uuid ?? '',
          client_location_id: selectedAgencyId,
        };
      })
      .filter((e) => e !== null && !existingEmails.has(e!.email.toLowerCase())) as Attendee[];
    if (toAdd.length > 0) onChange([...existing, ...toAdd]);
    setCheckedEmployees([]);
  };

  const agencyOptions = agenciesLoading
    ? [{ key: "", name: "Loading..." }]
    : agencies.map((ag) => ({ key: ag.id, name: ag.name }));

  return (
    <div className="w-full grid">
      <label className="self-stretch relative font-semibold dark:text-white mb-2">
        {isSingle ? "Agenda" : "External Attendees"}
      </label>

      {/* Agency / Employee lookup — Client Meeting only */}
      {!isSingle && isClientMeeting && (
        <div className="mb-4 grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:mq1275:grid-cols-2 mq575:mq1275:grid-cols-1 gap-[8px] min-w-full items-start">
         {
          !isDemoMeeting &&   <SearchableInputsDropdown
            label="Agency"
            placeHolder="Select Agency"
            options={agencyOptions}
            onSelect={handleAgencySelect}
            initialValue={selectedAgencyName}
          />
         }       

          {selectedAgencyId && (
            <div className="flex flex-col gap-2">
              <InputsDropdownMultipleSearchSelect
                label="Select Employees"
                placeHolder={employeesLoading ? "Loading..." : "Search by name or email"}
                options={employeeOptions}
                onSelect={handleEmployeeSelect}
                inputType="checkbox"
                checkedItems={checkedEmployees}
                disableDropdown={employeesLoading}
              />
              {checkedEmployees.length > 0 && (
                <button
                  type="button"
                  onClick={handleAddCheckedEmployees}
                  className="cursor-pointer self-start py-1.5 px-4 bg-surface-primary-surface text-white text-sm rounded hover:bg-dodgerblue"
                >
                  Add Selected ({checkedEmployees.length})
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Manual input fields */}
      {isSingle ? (
        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
          <div className="agendainput_blg inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid border-surface-border">
            <input
              type="text"
              placeholder="Agenda"
              className="border rounded px-2 py-1 flex-1"
              value={agenda}
              onChange={(e) => setAgenda(e.target.value)}
            />
          </div>
          <div className="w-full">
            <button
              type="button"
              onClick={handleAdd}
              className="cursor-pointer grid flex-col items-center justify-start py-[12px] px-[18px] plus-task-btn taskdash-plush-img rounded"
            >
              <img className="w-4 h-4 relative overflow-hidden shrink-0" loading="lazy" alt="" src={ioniconaaddoutline} />
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
          <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] agendainput_blg rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid border-surface-border">
            <input
              type="text"
              placeholder="Full name"
              className="border rounded px-2 py-1 flex-1"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="inputborder_css_dv mt-id-dv self-stretch shadow-[0px_1px_2px_rgba(16,_24,_40,_0.05)] agendainput_blg rounded-radius-md bg-text-text-tertiary overflow-hidden flex flex-row items-center justify-start py-2.5 px-[15px] dark:bg-slate-700 border-[1px] border-solid border-surface-border">
            <input
              type="email"
              placeholder="email@company.com"
              className="border rounded px-2 py-1 flex-1"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div>
            <button
              type="button"
              onClick={handleAdd}
              className="cursor-pointer grid flex-col items-center justify-start py-[12px] px-[18px] plus-task-btn taskdash-plush-img rounded"
            >
              <img className="w-4 h-4 relative overflow-hidden shrink-0" loading="lazy" alt="" src={ioniconaaddoutline} />
            </button>
          </div>
        </div>
      )}

      {/* Added attendees list */}
      <div className="mt-4 grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 mt-[8px] gap-[8px] min-w-full">
        {isSingle
          ? (value as Agenda[]).map((ag, index) => (
              <div key={index} className="flex justify-between items-center border rounded px-3 py-2 bg-gray-50 items-dark-blg">
                <span>{ag}</span>
                <button type="button" onClick={() => handleRemove(index)} className="cursor-pointer agenda-nner_btn">
                  <img src={closeDowpDowniconimg} className="w-4 h-4" alt="" />
                </button>
              </div>
            ))
          : (value as Attendee[]).map((attendee, index) => (
              <div key={index} className="flex justify-between items-center border rounded px-3 py-2 bg-gray-50 items-dark-blg">
                <span>
                  <strong>{attendee.name}</strong> — {attendee.email}
                </span>
                <button type="button" onClick={() => handleRemove(index)} className="cursor-pointer agenda-nner_btn">
                  <img src={closeDowpDowniconimg} className="w-4 h-4" alt="" />
                </button>
              </div>
            ))}
      </div>
    </div>
  );
};

export type { Attendee, Agenda };
export default ExternalAttendees;
